import type { Transition, Variants } from "framer-motion";

/**
 * Centralised motion vocabulary. Components import these rather than defining
 * ad-hoc values, so the whole site moves with one rhythm.
 *
 * Framer Motion reads `prefers-reduced-motion` through <MotionConfig reducedMotion="user">
 * in the root layout, which strips transforms and leaves opacity only.
 */

export const easeOut: Transition["ease"] = [0.16, 1, 0.3, 1];

/** Standard viewport trigger: animate once, when the element is meaningfully on screen. */
export const viewportOnce = { once: true, amount: 0.05, margin: "160px 0px" } as const;

/** Hero copy: a slightly larger reveal, staggered line by line. */
export const heroStagger: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.09, delayChildren: 0.05 },
  },
};

export const heroItem: Variants = {
  hidden: { opacity: 1, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: easeOut },
  },
};

/**
 * Section headings and body blocks.
 *
 * Transform and opacity only. These used to carry a blur filter on entry, which
 * reads nicely and is the single most expensive thing you can animate: a filter
 * forces a repaint of the layer on every frame rather than being handled by the
 * compositor, and these variants are applied to dozens of elements per page.
 * The travel and the fade carry the same intent for none of the cost.
 */
export const fadeUp: Variants = {
  // Opacity intentionally stays at 1. The server-rendered page is therefore
  // complete when JavaScript, IntersectionObserver, or motion setup fails;
  // capable browsers only enhance it with a short settling movement.
  hidden: { opacity: 1, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: easeOut },
  },
};

/**
 * Numbers and short labels: opacity only, no travel.
 *
 * The name is kept because it is imported in several places and describes the
 * intent; the blur itself is gone, for the reason above.
 */
export const blurIn: Variants = {
  hidden: { opacity: 1, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: easeOut },
  },
};

/** Wrapper for grids of cards. */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.045 },
  },
};

/** Restrained clip reveal for photography, no zoom, no drama. */
export const imageReveal: Variants = {
  hidden: { opacity: 1, clipPath: "inset(3% 0% 3% 0%)" },
  visible: {
    opacity: 1,
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.5, ease: easeOut },
  },
};

/** Gallery lightbox. */
export const lightboxBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const lightboxPanel: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: easeOut } },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
};

/** Mobile navigation drawer. */
export const drawerPanel: Variants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: easeOut } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18 } },
};
